import * as React from 'react';
import { useListAuditSignouts, useListEvents, type AuditEntry } from '@cdf/api-client-react';
import { PageHeader } from '@/components/layout/page-header';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { ScrollTextIcon, MessageSquareIcon, LogOutIcon, ClipboardCheckIcon } from 'lucide-react';

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function AuditLogPage() {
  const [eventId, setEventId] = React.useState<string | undefined>(undefined);
  const { data: eventsData } = useListEvents();
  const { data, isLoading } = useListAuditSignouts({ eventId });
  const entries = data?.entries ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Audit Log"
        description="Complete sign-out decision trail and parent SMS instruction history"
        icon={ScrollTextIcon}
      />
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium">Filter by event:</span>
        <Select value={eventId ?? 'all'} onValueChange={(v) => setEventId(v === 'all' ? undefined : v)}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="All events" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All events</SelectItem>
            {eventsData?.events.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Type</TableHead>
              <TableHead>Time</TableHead>
              <TableHead>Student</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Actor / Sender</TableHead>
              <TableHead>Message</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground py-8 text-center">
                  Loading audit log…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && entries.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground py-8 text-center">
                  No audit entries yet.
                </TableCell>
              </TableRow>
            )}
            {entries.map((entry: AuditEntry, i: number) => (
              <TableRow key={`${entry.type}-${i}`}>
                <TableCell>
                  {entry.type === 'signout' ? (
                    <LogOutIcon className="size-4 text-muted-foreground" />
                  ) : entry.type === 'roll_call' ? (
                    <ClipboardCheckIcon className="size-4 text-muted-foreground" />
                  ) : (
                    <MessageSquareIcon className="size-4 text-muted-foreground" />
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                  {formatTime(entry.timestamp)}
                </TableCell>
                <TableCell className="font-medium">{entry.studentName ?? '—'}</TableCell>
                <TableCell className="text-muted-foreground">{entry.eventName ?? '—'}</TableCell>
                <TableCell>
                  <span className="text-sm">{entry.action ?? '—'}</span>
                  {entry.checkoutType && (
                    <Badge variant="outline" className="ml-2 text-xs">
                      {entry.checkoutType}
                    </Badge>
                  )}
                  {entry.authorizedPersonName && (
                    <Badge variant="outline" className="ml-2 text-xs">
                      with {entry.authorizedPersonName}
                    </Badge>
                  )}
                  {entry.rollCallStatus && (
                    <Badge
                      variant="outline"
                      className={`ml-2 text-xs ${
                        entry.rollCallStatus === 'present'
                          ? 'border-success/30 text-success'
                          : 'border-destructive/30 text-destructive'
                      }`}
                    >
                      {entry.rollCallStatus}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {entry.actor ?? '—'}
                </TableCell>
                <TableCell className="max-w-xs text-sm">
                  {entry.messageBody ? (
                    <span className="italic text-muted-foreground">"{entry.messageBody}"</span>
                  ) : (
                    '—'
                  )}
                  {entry.isValidated === false && (
                    <Badge variant="destructive" className="ml-2 text-xs">
                      Unvalidated
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
